/**
 * ManagerSchedulePanel — Embedded inside the Technician Detail dialog.
 * Lets manager / owner / admin add, edit, and remove schedule entries
 * for a specific technician. Reads & writes the same `calendar_events`
 * collection used by the technician's own Schedule screen, so changes
 * are reflected in both views in real time.
 *
 * Backend automatically push-notifies the technician whenever a manager
 * creates/updates/removes one of their schedule entries.
 */
import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Save, Trash2, Pencil, Loader2, CalendarDays } from "lucide-react";
import { toast } from "sonner";

const EVENT_TYPES = [
  { v: "job",         label: "Job" },
  { v: "appointment", label: "Appointment" },
  { v: "inspection",  label: "Inspection" },
  { v: "follow_up",   label: "Follow-up" },
  { v: "other",       label: "Other" },
];

const todayIso = () => new Date().toISOString().split("T")[0];

const toDateField = (iso) => {
  if (!iso) return todayIso();
  try { return new Date(iso).toISOString().split("T")[0]; } catch { return todayIso(); }
};
const toTimeField = (iso) => {
  if (!iso) return "09:00";
  try {
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  } catch { return "09:00"; }
};

const blankForm = () => ({
  id: null,
  title: "",
  event_type: "job",
  request_id: "",
  date: todayIso(),
  start_time: "09:00",
  end_time: "10:00",
  notes: "",
});

export default function ManagerSchedulePanel({ vendorId, vendorName }) {
  const [events, setEvents] = useState([]);
  const [jobs, setJobs] = useState([]); // active jobs for this vendor
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(blankForm());

  const load = useCallback(async () => {
    if (!vendorId) return;
    setLoading(true);
    try {
      // Pull a wide window so manager sees past + upcoming entries
      const start = new Date(); start.setDate(start.getDate() - 7);
      const end   = new Date(); end.setDate(end.getDate() + 60);
      const [evRes, jobRes] = await Promise.allSettled([
        API.get(`/calendar/${vendorId}`, {
          params: { start: start.toISOString(), end: end.toISOString() },
        }),
        API.get(`/management/requests?limit=200`),
      ]);
      if (evRes.status === "fulfilled") {
        setEvents((evRes.value.data.events || []).filter(e => !e.is_deleted));
      }
      if (jobRes.status === "fulfilled") {
        const all = jobRes.value.data.requests || [];
        // Active jobs for this technician — useful for "Link to job" picker
        setJobs(all.filter((r) =>
          r.assigned_vendor_id === vendorId &&
          ["offered", "accepted", "en_route", "in_progress"].includes(r.current_status)
        ));
      }
    } catch (err) {
      console.error("manager-schedule load failed", err);
    } finally {
      setLoading(false);
    }
  }, [vendorId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm(blankForm());
    setShowForm(true);
  };
  const openEdit = (ev) => {
    setForm({
      id: ev.id,
      title: ev.title || "",
      event_type: ev.event_type || "appointment",
      request_id: ev.request_id || "",
      date: toDateField(ev.start_datetime),
      start_time: toTimeField(ev.start_datetime),
      end_time: toTimeField(ev.end_datetime),
      notes: ev.description || "",
    });
    setShowForm(true);
  };

  const onPickJob = (val) => {
    if (val === "none") {
      setForm((f) => ({ ...f, request_id: "" }));
      return;
    }
    const job = jobs.find((j) => j.id === val);
    if (!job) {
      setForm((f) => ({ ...f, request_id: val }));
      return;
    }
    setForm((f) => ({
      ...f,
      request_id: job.id,
      title: f.title?.trim() ? f.title : (job.service_type_label || "Job visit"),
      event_type: "job",
    }));
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Please enter a title"); return; }
    if (!form.date)         { toast.error("Pick a date"); return; }
    const start = new Date(`${form.date}T${form.start_time || "09:00"}`);
    const end   = new Date(`${form.date}T${form.end_time   || "10:00"}`);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      toast.error("Invalid date/time"); return;
    }
    if (end <= start) { toast.error("End time must be after start time"); return; }

    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.notes || "",
        event_type: form.event_type,
        request_id: form.request_id || null,
        vendor_id: vendorId,
        start_datetime: start.toISOString(),
        end_datetime: end.toISOString(),
        all_day: false,
        status: "scheduled",
      };
      if (form.id) {
        await API.patch(`/calendar/${form.id}`, payload);
        toast.success("Schedule entry updated — technician notified");
      } else {
        await API.post("/calendar", payload);
        toast.success("Schedule entry added — technician notified");
      }
      setShowForm(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (ev) => {
    if (!window.confirm(`Remove "${ev.title}" from ${vendorName || "this technician"}'s schedule?`)) return;
    try {
      await API.delete(`/calendar/${ev.id}`);
      toast.success("Removed — technician notified");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Remove failed");
    }
  };

  const fmtRange = (ev) => {
    try {
      const s = new Date(ev.start_datetime);
      const e = new Date(ev.end_datetime);
      const date = s.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
      const tFmt = { hour: "numeric", minute: "2-digit" };
      return `${date} · ${s.toLocaleTimeString(undefined, tFmt)} – ${e.toLocaleTimeString(undefined, tFmt)}`;
    } catch { return ev.start_datetime || ""; }
  };

  return (
    <div className="space-y-3" data-testid="manager-schedule-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <CalendarDays className="w-4 h-4 text-zinc-500" />
          <h4 className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Schedule</h4>
          <span className="text-[10px] text-zinc-400">({events.length})</span>
        </div>
        <Button size="sm"
                onClick={openCreate}
                className="h-7 bg-red-600 hover:bg-red-700 text-white text-xs"
                data-testid="mgr-sched-add-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-3 text-xs text-zinc-400">Loading…</div>
      ) : events.length === 0 ? (
        <div className="text-center py-4 text-xs text-zinc-400 border border-dashed rounded-lg">
          No schedule entries yet.
        </div>
      ) : (
        <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
          {events.map((ev) => (
            <div key={ev.id}
                 className="border border-zinc-200 rounded-lg p-2 bg-white flex items-start gap-2"
                 data-testid={`mgr-sched-row-${ev.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-zinc-900 truncate">{ev.title}</p>
                <p className="text-[10px] text-zinc-500">{fmtRange(ev)}</p>
                {ev.description && (
                  <p className="text-[10px] text-zinc-400 truncate">{ev.description}</p>
                )}
                <div className="flex items-center gap-1.5 mt-1">
                  <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600">
                    {ev.event_type}
                  </span>
                  {ev.request_id && (
                    <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">
                      Linked job
                    </span>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <Button size="sm" variant="ghost" className="h-6 w-6 p-0"
                        onClick={() => openEdit(ev)}
                        data-testid={`mgr-sched-edit-${ev.id}`}>
                  <Pencil className="w-3 h-3 text-zinc-500" />
                </Button>
                <Button size="sm" variant="ghost" className="h-6 w-6 p-0"
                        onClick={() => remove(ev)}
                        data-testid={`mgr-sched-remove-${ev.id}`}>
                  <Trash2 className="w-3 h-3 text-rose-500" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="border border-red-200 rounded-lg p-2.5 bg-red-50/30 space-y-2" data-testid="mgr-sched-form">
          <p className="text-[10px] uppercase font-bold text-red-700">
            {form.id ? "Edit entry" : "New entry"}{vendorName ? ` for ${vendorName}` : ""}
          </p>

          {jobs.length > 0 && (
            <div>
              <label className="text-[10px] uppercase text-zinc-500">Link to active job (optional)</label>
              <Select value={form.request_id || "none"} onValueChange={onPickJob}>
                <SelectTrigger data-testid="mgr-sched-job-picker" className="h-8 text-xs">
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No active job</SelectItem>
                  {jobs.map((j) => (
                    <SelectItem key={j.id} value={j.id}>
                      {j.service_type_label} — {j.customer_name || "Customer"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <label className="text-[10px] uppercase text-zinc-500">Title</label>
            <Input value={form.title}
                   onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                   placeholder="Title (e.g. 'Initial site visit')"
                   className="h-8 text-xs"
                   data-testid="mgr-sched-title" />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase text-zinc-500">Date</label>
              <Input type="date" value={form.date}
                     onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                     className="h-8 text-xs" data-testid="mgr-sched-date" />
            </div>
            <div>
              <label className="text-[10px] uppercase text-zinc-500">Type</label>
              <Select value={form.event_type}
                      onValueChange={v => setForm(f => ({ ...f, event_type: v }))}>
                <SelectTrigger data-testid="mgr-sched-type" className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EVENT_TYPES.map(t => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase text-zinc-500">Start time</label>
              <Input type="time" value={form.start_time}
                     onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))}
                     className="h-8 text-xs" data-testid="mgr-sched-start" />
            </div>
            <div>
              <label className="text-[10px] uppercase text-zinc-500">End time</label>
              <Input type="time" value={form.end_time}
                     onChange={e => setForm(f => ({ ...f, end_time: e.target.value }))}
                     className="h-8 text-xs" data-testid="mgr-sched-end" />
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase text-zinc-500">Notes</label>
            <Textarea rows={2} value={form.notes}
                      onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                      placeholder="Optional notes for the technician"
                      className="text-xs"
                      data-testid="mgr-sched-notes" />
          </div>

          <div className="flex gap-2 pt-1">
            <Button size="sm" variant="outline" className="flex-1 h-8 text-xs"
                    onClick={() => setShowForm(false)}>Cancel</Button>
            <Button size="sm"
                    onClick={save}
                    disabled={saving}
                    className="flex-1 h-8 bg-red-600 hover:bg-red-700 text-white text-xs"
                    data-testid="mgr-sched-save">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <><Save className="w-3.5 h-3.5 mr-1" /> {form.id ? "Update" : "Save"}</>}
            </Button>
          </div>
          <p className="text-[10px] text-zinc-500 italic">
            Saving will send a push notification to the technician.
          </p>
        </div>
      )}
    </div>
  );
}
